import type { Locale as dateFnsLocaleType } from 'date-fns';
import arLocale from 'date-fns/locale/ar-SA';
import enUsLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import faIrLocale from 'date-fns/locale/fa-IR';
import frLocale from 'date-fns/locale/fr';
import hiLocale from 'date-fns/locale/hi';
import htLocale from 'date-fns/locale/ht';
import huLocale from 'date-fns/locale/hu';
import koLocale from 'date-fns/locale/ko';
import nlLocale from 'date-fns/locale/nl';
import plLocale from 'date-fns/locale/pl';
import ptLocale from 'date-fns/locale/pt';
import ruLocale from 'date-fns/locale/ru';
import ukLocale from 'date-fns/locale/uk';
import viLocale from 'date-fns/locale/vi';
import zhCnLocale from 'date-fns/locale/zh-CN';
import zhTwLocale from 'date-fns/locale/zh-TW';

// Partially implemented locales not supported by date-fns
import { amLocale } from './dateFnsLocales/amLocale/locale';
import { filLocale } from './dateFnsLocales/filLocale/locale';
import { keaLocale } from './dateFnsLocales/keaLocale/locale';
import { kmLocale } from './dateFnsLocales/kmLocale/locale';
import { soLocale } from './dateFnsLocales/soLocale/locale';
import { urLocale } from './dateFnsLocales/urLocale/locale';

// FIXME: Locale code set in locale cookie. Must match to supported Django language codes.
// monoweb/django_apps/core/translations.py
export enum Locale {
  ENGLISH = 'en-US',
  SPANISH = 'es-US',
  CHINESE_SIMPLIFIED = 'zh-CN',
  CHINESE_TRADITIONAL = 'zh-hant',
  PORTUGUESE = 'pt-PT',
  HAITIAN_CREOLE = 'ht',
  KOREAN = 'ko',
  RUSSIAN = 'ru-RU',
  VIETNAMESE = 'vi',
  ARABIC = 'ar-SA',
  FILIPINO = 'fil',
  SAMOAN = 'sm',
  KHMER = 'km',
  POLISH = 'pl',
  UKRAINIAN = 'uk',
  URDU = 'ur',
  FRENCH = 'fr',
  SOMALI = 'so',
  FARSI = 'fa',
  CAPE_VERDEAN_CREOLE = 'kea',
  AMHARIC = 'am',
  MARSHALLESE = 'mh',
  HMONG = 'hmn',
  HINDI = 'hi',
  HUNGARIAN = 'hu',
  DUTCH = 'nl',
}

export const ALL_LOCALES: Locale[] = Object.values(Locale);

export const RIGHT_TO_LEFT_LOCALES: Set<Locale> = new Set();
RIGHT_TO_LEFT_LOCALES.add(Locale.ARABIC);
RIGHT_TO_LEFT_LOCALES.add(Locale.URDU);
RIGHT_TO_LEFT_LOCALES.add(Locale.FARSI);

export const DEFAULT_LOCALE: Locale = Locale.ENGLISH;

// Locales that we want to work with in some way, but that are not supported in
// Django or JS libraries or our localization system.
// Using codes from ISO 639-3.
export enum ExtraLocale {
  AMERICAN_SIGN_LANGUAGE = 'ase',
}

// https://github.com/moment/moment/tree/2.24.0/locale
export const MOMENT_LOCALE_NAMES: {
  [name in Locale]: string;
} = {
  [Locale.ENGLISH]: 'en',
  [Locale.SPANISH]: 'es',
  [Locale.CHINESE_SIMPLIFIED]: 'zh-cn',
  [Locale.PORTUGUESE]: 'pt',
  [Locale.HAITIAN_CREOLE]: 'ht',
  [Locale.KOREAN]: 'ko',
  [Locale.RUSSIAN]: 'ru',
  [Locale.VIETNAMESE]: 'vi',
  [Locale.ARABIC]: 'ar',
  [Locale.FILIPINO]: 'tl-ph',
  [Locale.CHINESE_TRADITIONAL]: 'zh-tw',
  [Locale.KHMER]: 'km',
  [Locale.POLISH]: 'pl',
  [Locale.UKRAINIAN]: 'uk',
  [Locale.URDU]: 'ur',
  [Locale.FRENCH]: 'fr',
  [Locale.FARSI]: 'fa',
  [Locale.HINDI]: 'hi',
  [Locale.HUNGARIAN]: 'hu',
  [Locale.DUTCH]: 'nl',
  // locales not supported in moment
  [Locale.SAMOAN]: 'en',
  [Locale.SOMALI]: 'en',
  [Locale.CAPE_VERDEAN_CREOLE]: 'en',
  [Locale.AMHARIC]: 'en',
  [Locale.MARSHALLESE]: 'en',
  [Locale.HMONG]: 'en',
};

// https://github.com/date-fns/date-fns/tree/v2.27.0/src/locale
export const DATE_FNS_LOCALES: {
  [name in Locale]: dateFnsLocaleType;
} = {
  [Locale.ENGLISH]: enUsLocale,
  [Locale.SPANISH]: esLocale,
  [Locale.CHINESE_SIMPLIFIED]: zhCnLocale,
  [Locale.PORTUGUESE]: ptLocale,
  [Locale.HAITIAN_CREOLE]: htLocale,
  [Locale.KOREAN]: koLocale,
  [Locale.RUSSIAN]: ruLocale,
  [Locale.VIETNAMESE]: viLocale,
  [Locale.ARABIC]: arLocale,
  [Locale.CHINESE_TRADITIONAL]: zhTwLocale,
  [Locale.POLISH]: plLocale,
  [Locale.UKRAINIAN]: ukLocale,
  [Locale.FRENCH]: frLocale,
  [Locale.FARSI]: faIrLocale,
  [Locale.HINDI]: hiLocale,
  [Locale.HUNGARIAN]: huLocale,
  [Locale.DUTCH]: nlLocale,
  // locales not yet supported in date-fns
  [Locale.SAMOAN]: enUsLocale,
  [Locale.MARSHALLESE]: enUsLocale,
  [Locale.HMONG]: enUsLocale,
  // locales not yet supported in date-fns with custom overrides
  [Locale.FILIPINO]: filLocale,
  [Locale.KHMER]: kmLocale,
  [Locale.CAPE_VERDEAN_CREOLE]: keaLocale,
  [Locale.SOMALI]: soLocale,
  [Locale.AMHARIC]: amLocale,
  [Locale.URDU]: urLocale,
};

export const LOCALE_DISPLAY_NAMES: {
  [name in Locale | ExtraLocale]: string;
} = {
  [Locale.ENGLISH]: 'English',
  [Locale.SPANISH]: 'Español',
  [Locale.CHINESE_SIMPLIFIED]: '简体中文',
  [Locale.PORTUGUESE]: 'Português',
  [Locale.HAITIAN_CREOLE]: 'Kreyòl Ayisyen',
  [Locale.KOREAN]: '한국어',
  [Locale.RUSSIAN]: 'русский',
  [Locale.VIETNAMESE]: 'Tiếng Việt',
  [Locale.ARABIC]: 'العربية',
  [Locale.FILIPINO]: 'Filipino',
  [Locale.CHINESE_TRADITIONAL]: '繁體中文',
  [Locale.SAMOAN]: "Gagana fa'a Samoa",
  [Locale.KHMER]: 'ខ្មែរ',
  [Locale.POLISH]: 'Polski',
  [Locale.UKRAINIAN]: 'українська',
  [Locale.URDU]: 'اُردُو',
  [Locale.FRENCH]: 'Français',
  [Locale.FARSI]: 'فارسی',
  [Locale.SOMALI]: 'Soomaali',
  [Locale.CAPE_VERDEAN_CREOLE]: 'Kriolu',
  [Locale.AMHARIC]: 'አማርኛ',
  [Locale.MARSHALLESE]: 'kajin Majöl',
  [Locale.HINDI]: 'हिन्दी',
  [Locale.HMONG]: 'Hmoob',
  [ExtraLocale.AMERICAN_SIGN_LANGUAGE]: 'American Sign Language (ASL)',
  [Locale.HUNGARIAN]: 'Magyar',
  [Locale.DUTCH]: 'Nederlands',
};

export const SHORT_LOCALE_DISPLAY_NAMES: {
  [name in Locale]: string;
} = {
  [Locale.ENGLISH]: 'EN',
  [Locale.SPANISH]: 'ES',
  [Locale.CHINESE_SIMPLIFIED]: '简体中文',
  [Locale.PORTUGUESE]: 'PT',
  [Locale.HAITIAN_CREOLE]: 'HT',
  [Locale.KOREAN]: '한국어',
  [Locale.RUSSIAN]: 'RU',
  [Locale.VIETNAMESE]: 'VI',
  [Locale.ARABIC]: 'AR',
  [Locale.FILIPINO]: 'KWF',
  [Locale.CHINESE_TRADITIONAL]: '繁體中文',
  [Locale.SAMOAN]: 'SM',
  [Locale.KHMER]: 'KM',
  [Locale.POLISH]: 'PL',
  [Locale.UKRAINIAN]: 'UK',
  [Locale.URDU]: 'UR',
  [Locale.FRENCH]: 'FR',
  [Locale.FARSI]: 'FA',
  [Locale.SOMALI]: 'SO',
  [Locale.CAPE_VERDEAN_CREOLE]: 'KEA',
  [Locale.AMHARIC]: 'AM',
  [Locale.MARSHALLESE]: 'MH',
  [Locale.HMONG]: 'HMN',
  [Locale.HINDI]: 'HI',
  [Locale.HUNGARIAN]: 'HU',
  [Locale.DUTCH]: 'NL',
};

export const LOCALE_ENGLISH_NAMES: {
  [name in Locale]: string;
} = {
  [Locale.ENGLISH]: 'English',
  [Locale.SPANISH]: 'Spanish',
  [Locale.CHINESE_SIMPLIFIED]: 'Simplified Chinese',
  [Locale.PORTUGUESE]: 'Portuguese',
  [Locale.HAITIAN_CREOLE]: 'Haitian Creole',
  [Locale.KOREAN]: 'Korean',
  [Locale.RUSSIAN]: 'Russian',
  [Locale.VIETNAMESE]: 'Vietnamese',
  [Locale.ARABIC]: 'Arabic',
  [Locale.FILIPINO]: 'Filipino',
  [Locale.CHINESE_TRADITIONAL]: 'Traditional Chinese',
  [Locale.SAMOAN]: 'Samoan',
  [Locale.KHMER]: 'Khmer',
  [Locale.POLISH]: 'Polish',
  [Locale.UKRAINIAN]: 'Ukrainian',
  [Locale.URDU]: 'Urdu',
  [Locale.FRENCH]: 'French',
  [Locale.FARSI]: 'Farsi',
  [Locale.SOMALI]: 'Somali',
  [Locale.CAPE_VERDEAN_CREOLE]: 'Cape Verdean Creole',
  [Locale.AMHARIC]: 'Amharic',
  [Locale.MARSHALLESE]: 'Marshallese',
  [Locale.HMONG]: 'Hmong',
  [Locale.HINDI]: 'Hindi',
  [Locale.HUNGARIAN]: 'Hungarian',
  [Locale.DUTCH]: 'Dutch',
};

export const FRONTEND_TO_DJANGO_LOCALES: Record<Locale, string> = {
  [Locale.ENGLISH]: 'en',
  [Locale.SPANISH]: 'es',
  [Locale.CHINESE_SIMPLIFIED]: 'zh-hans',
  [Locale.PORTUGUESE]: 'pt',
  [Locale.HAITIAN_CREOLE]: 'ht',
  [Locale.KOREAN]: 'ko',
  [Locale.RUSSIAN]: 'ru',
  [Locale.VIETNAMESE]: 'vi',
  [Locale.ARABIC]: 'ar',
  [Locale.FILIPINO]: 'fil',
  [Locale.CHINESE_TRADITIONAL]: 'zh-hant',
  [Locale.SAMOAN]: 'sm',
  [Locale.KHMER]: 'km',
  [Locale.POLISH]: 'pl',
  [Locale.UKRAINIAN]: 'uk',
  [Locale.URDU]: 'ur',
  [Locale.FRENCH]: 'fr',
  [Locale.FARSI]: 'fa',
  [Locale.SOMALI]: 'so',
  [Locale.CAPE_VERDEAN_CREOLE]: 'kea',
  [Locale.AMHARIC]: 'am',
  [Locale.MARSHALLESE]: 'mh',
  [Locale.HMONG]: 'hmn',
  [Locale.HINDI]: 'hi',
  [Locale.HUNGARIAN]: 'hu',
  [Locale.DUTCH]: 'nl',
};

type FrontEndToDjangoLocalesMapType = typeof FRONTEND_TO_DJANGO_LOCALES;

export const DJANGO_TO_FRONTEND_LOCALES: { [key: string]: Locale } = Object.fromEntries(
  (
    Object.entries(FRONTEND_TO_DJANGO_LOCALES) as {
      [K in keyof FrontEndToDjangoLocalesMapType]: [Locale, FrontEndToDjangoLocalesMapType[Locale]];
    }[keyof FrontEndToDjangoLocalesMapType][]
  ).map(([frontendLocale, djangoLocale]) => [djangoLocale, frontendLocale])
);

/**
 * Define these values locally instead of using the browsers Intl
 * object, which requires some heavy polyfills.
 */
export enum ColorIso639Code {
  ENGLISH = 'en',
  SPANISH = 'es',
  CHINESE_SIMPLIFIED = 'zh_Hans',
  CHINESE_TRADITIONAL = 'zh_Hant',
  PORTUGUESE = 'pt',
  HAITIAN_CREOLE = 'ht',
  KOREAN = 'ko',
  RUSSIAN = 'ru',
  VIETNAMESE = 'vi',
  ARABIC = 'ar',
  FILIPINO = 'fil',
  SAMOAN = 'sm',
  KHMER = 'km',
  POLISH = 'pl',
  UKRAINIAN = 'uk',
  URDU = 'ur',
  FRENCH = 'fr',
  SOMALI = 'so',
  FARSI = 'fa',
  CAPE_VERDEAN_CREOLE = 'kea',
  AMHARIC = 'am',
  MARSHALLESE = 'mh',
  HMONG = 'hmn',
  HINDI = 'hi',
  HUNGARIAN = 'hu',
  DUTCH = 'nl',
}

/**
 * Translation message files are named with the language code, without country code.
 * This historical naming convention is based on our use of Django i18n features,
 * specifically that the Django PO file names are generated based on Django settings:
 * > from django.conf.global_settings import LANGUAGES
 *
 * FIXME: Reconcile with Locale, migrate file names in code and on Lokalise.
 * Our Locales should have both language and country code and these files should reflect that.
 */
export const COLOR_ISO_639_CODE_BY_LOCALE: Record<Locale, ColorIso639Code> = {
  [Locale.ENGLISH]: ColorIso639Code.ENGLISH,
  [Locale.SPANISH]: ColorIso639Code.SPANISH,
  [Locale.CHINESE_SIMPLIFIED]: ColorIso639Code.CHINESE_SIMPLIFIED,
  [Locale.CHINESE_TRADITIONAL]: ColorIso639Code.CHINESE_TRADITIONAL,
  [Locale.PORTUGUESE]: ColorIso639Code.PORTUGUESE,
  [Locale.HAITIAN_CREOLE]: ColorIso639Code.HAITIAN_CREOLE,
  [Locale.KOREAN]: ColorIso639Code.KOREAN,
  [Locale.RUSSIAN]: ColorIso639Code.RUSSIAN,
  [Locale.VIETNAMESE]: ColorIso639Code.VIETNAMESE,
  [Locale.ARABIC]: ColorIso639Code.ARABIC,
  [Locale.FILIPINO]: ColorIso639Code.FILIPINO,
  [Locale.SAMOAN]: ColorIso639Code.SAMOAN,
  [Locale.KHMER]: ColorIso639Code.KHMER,
  [Locale.POLISH]: ColorIso639Code.POLISH,
  [Locale.UKRAINIAN]: ColorIso639Code.UKRAINIAN,
  [Locale.URDU]: ColorIso639Code.URDU,
  [Locale.FRENCH]: ColorIso639Code.FRENCH,
  [Locale.SOMALI]: ColorIso639Code.SOMALI,
  [Locale.FARSI]: ColorIso639Code.FARSI,
  [Locale.CAPE_VERDEAN_CREOLE]: ColorIso639Code.CAPE_VERDEAN_CREOLE,
  [Locale.AMHARIC]: ColorIso639Code.AMHARIC,
  [Locale.MARSHALLESE]: ColorIso639Code.MARSHALLESE,
  [Locale.HMONG]: ColorIso639Code.HMONG,
  [Locale.HINDI]: ColorIso639Code.HINDI,
  [Locale.HUNGARIAN]: ColorIso639Code.HUNGARIAN,
  [Locale.DUTCH]: ColorIso639Code.DUTCH,
};
